import React, { useState, useEffect } from 'react';
import { instituteInfo, navLinks } from '../data/mock';
import { Separator } from './ui/separator';
import { Phone, Mail, MapPin, Facebook, ArrowUp, Heart } from 'lucide-react';

export const Footer = () => {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > window.innerHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const el = document.getElementById(href.substring(1));
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-slate-900">
      {/* Top CTA bar */}
      <div className="relative bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-1">Ready to Excel?</h3>
            <p className="text-teal-100">Admissions open for all classes. Limited seats available!</p>
          </div>
          <a href={`tel:${instituteInfo.phone}`}>
            <button className="flex items-center gap-2 px-8 py-4 bg-white text-teal-700 font-semibold rounded-full hover:bg-teal-50 transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-teal-700/20">
              <Phone className="w-5 h-5" />
              Call for Admission
            </button>
          </a>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* About */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/logo.png"
                alt="ENIAC Logo"
                className="w-12 h-12 object-contain"
              />
              <div>
                <span className="font-bold text-xl text-white">{instituteInfo.name}</span>
                <span className="block text-[10px] font-medium tracking-widest uppercase text-teal-400">Coaching Centre</span>
              </div>
            </div>
            <p className="text-slate-400 leading-relaxed text-sm mb-5">
              A renowned coaching centre in Bardhaman providing quality education by experienced teachers.
            </p>
            <a
              href={instituteInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors duration-300"
            >
              <Facebook className="w-4 h-4" />
              Follow us on Facebook
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-5">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left text-sm text-slate-400 hover:text-teal-400 transition-colors duration-300"
                >
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-white font-semibold mb-5">Programs</h4>
            <nav className="flex flex-col gap-3">
              {['CBSE Coaching', 'ICSE Coaching', 'WBBSE Coaching', 'Computer Training', 'All Subjects (KG-X)'].map((item) => (
                <span key={item} className="text-sm text-slate-400">{item}</span>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-5">Contact Info</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-400">{instituteInfo.address}</span>
              </div>
              <a href={`tel:${instituteInfo.phone}`} className="flex items-center gap-3 text-sm text-slate-400 hover:text-teal-400 transition-colors">
                <Phone className="w-4 h-4 text-teal-400 flex-shrink-0" />
                {instituteInfo.phone}
              </a>
              <a href={`mailto:${instituteInfo.email}`} className="flex items-center gap-3 text-sm text-slate-400 hover:text-teal-400 transition-colors">
                <Mail className="w-4 h-4 text-teal-400 flex-shrink-0" />
                {instituteInfo.email}
              </a>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-slate-800" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} {instituteInfo.fullName}. All rights reserved.
          </p>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            Made with <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400" /> by{' '}
            <a
              href="YOUR_AGENCY_URL_HERE"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-400 hover:text-teal-300 font-semibold transition-colors duration-300"
            >
              OryzonX
            </a>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-teal-600 text-white shadow-lg shadow-teal-600/25 hover:bg-teal-500 transition-all duration-300 hover:-translate-y-1 flex items-center justify-center group ${
        showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform duration-300" />
      </button>
    </footer>
  );
};
